import type { Id } from "@convex/_generated/dataModel";
import { useMutation } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { CheckCircle2, CreditCard, Home, Monitor, Tablet } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

import {
  type DevicesListResult,
  locationsUpdateEndOfDayConfig,
} from "~/api-refs";
import { RadioPanels } from "~/components/shared/radio-panels";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { ProductEvents } from "~/lib/posthog";
import { paymentTypeOptions } from "~/lib/validators/pos-device-payment-terminal";

interface DevicesListProps {
  data: DevicesListResult;
}

export function DevicesList({ data }: DevicesListProps) {
  const { id } = useParams({ from: "/_authenticated/locations/$id/devices" });

  const [query, setQuery] = useState("");

  const filteredData = data?.devices.filter(
    (device) =>
      !device.name || device.name.toLowerCase().includes(query.toLowerCase())
  );

  const updateEndOfDayConfigMutation = useMutation({
    mutationFn: (mutationData: {
      locationId: Id<"locations">;
      deviceLevel: "location" | "pos";
    }) => convexClient.mutation(locationsUpdateEndOfDayConfig, mutationData),
    onSuccess: () => {
      ProductEvents.settingsUpdated("end-of-day-config");
    },
  });

  return (
    <div>
      <div className="w-1/2">
        <RadioPanels
          items={[
            {
              value: "location",
              label: "Location Level",
              description:
                "Start and end the day on any POS device for the entire location",
              icon: Home,
            },
            {
              value: "pos",
              label: "Device Level",
              description:
                "Start and end the day individually on each POS device at this location",
              icon: Monitor,
            },
          ]}
          onValueChange={(value) => {
            updateEndOfDayConfigMutation.mutate({
              locationId: toConvexTableId("locations", id),
              deviceLevel: value,
            });

            toast.success(
              <div className="flex flex-col items-center justify-center gap-2">
                <div className="flex flex-row items-center justify-center gap-3">
                  <div className="-mt-8 flex items-start justify-start">
                    <CheckCircle2 className="mt-0 ml-0" />
                  </div>
                  <div className="ml-0 flex flex-col">
                    <p className="body-sm subtitle-sm text-left">
                      Start / End of Day updated
                    </p>
                    <p className="body-sm text-muted-foreground text-left">
                      Start and End of Day processes will affect all devices at
                      this location now.
                    </p>
                  </div>
                </div>
              </div>
            );
          }}
          value={data?.showOrderByDevice ? "pos" : "location"}
        />
      </div>
      <div className="flex justify-between py-5" id="manage-devices">
        <div className="space-y-1">
          <h2 className="subtitle-sm text-foreground">Manage POS Devices</h2>
          <p className="body-sm text-foreground leading-tight">
            View or remove devices and configure payment terminals
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            className="w-80 rounded-xl"
            onChange={(e) => {
              setQuery(e.target.value);
            }}
            placeholder="Search device name..."
            value={query}
          />
          <Button
            onClick={() => {
              toast.success("Devices list refreshed");
            }}
            variant="outline"
          >
            Refresh
          </Button>
          <Button
            onClick={() => {
              toast.info("Add Device functionality coming soon");
            }}
          >
            Add Device
          </Button>
        </div>
      </div>
      <div className="space-y-5">
        {filteredData && filteredData.length > 0 ? (
          filteredData.map((device) => (
            <DeviceCard device={device} key={device._id} />
          ))
        ) : (
          <div className="border-border bg-muted flex flex-col items-center justify-center rounded-xl border p-12">
            <Tablet className="text-muted-foreground mb-4 size-16" />
            <h3 className="subtitle subtitle-sm text-foreground mb-2">
              No devices found
            </h3>
            <p className="body-sm text-muted-foreground mb-4 text-center">
              {query
                ? `No devices match "${query}". Try a different search term.`
                : 'No POS devices are registered for this location yet. Click "Add Device" to get started.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function DeviceCard({
  device,
}: {
  device: DevicesListResult["devices"][number];
}) {
  const { id } = useParams({ from: "/_authenticated/locations/$id/devices" });

  return (
    <div className="border-border inline-flex w-full items-center justify-start gap-4 rounded-xl border p-4">
      <div className="flex w-full justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex w-60 items-center space-x-3">
            <Tablet className="size-8" />
            <p>{device.name}</p>
          </div>
          <div className="w-32">
            <Badge
              className="flex w-fit items-center space-x-1 px-4 py-1"
              variant={device.active ? "success" : "destructive"}
            >
              <div
                className={twMerge(
                  "h-1.5 w-1.5 rounded-full",
                  device.active ? "bg-success" : "bg-destructive/50"
                )}
              />
              <p>{device.active ? "Active" : "Inactive"}</p>
            </Badge>
          </div>
          <div className="flex items-center space-x-2">
            <CreditCard className="size-6" />
            <p>
              {device?.paymentTerminal
                ? paymentTypeOptions[device.paymentTerminal.type].title
                : "-"}
            </p>
          </div>
        </div>

        <Button asChild variant="secondary">
          <Link
            params={{ id, deviceId: device._id }}
            to="/locations/$id/devices/$deviceId"
          >
            Manage
          </Link>
        </Button>
      </div>
    </div>
  );
}
